import { Button, Typography } from "@material-ui/core"
import { useIsDesktop } from "common/MediaQueries"
import { trackCheckoutStep } from "common/analytics"
import * as orderRepository from "components/shopFeatures/order/orderRepository"
import Head from "next/head"
import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import routes from "routes"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import { Font } from "../wizard/constants"
import PaymentFrame from "./PaymentFrame"
import ErrorDialog from "./error/ErrorDialog"
import {
  clearCreditGuardUrl,
  fetchPaymentUrl,
  resetTransactionComplete,
} from "./paymentSlice"

const ENV = process.env.ENV

const StyledHeading = styled(Typography).attrs({ variant: "h2" })`
  ${(p) => p.theme.breakpoints.down("sm")} {
    text-align: center;
    margin-top: 40px;
  }
`

const StyledHeadingContainer = styled.div`
  display: flex;
  justify-content: center;
`

const StyledImageButton = styled(Button)`
  position: absolute;
  right: 5px;
  top: 15px;
`

const StyledText = styled(Typography)`
  font-family: ${Font.OENaPro};
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: center;
  margin-bottom: 20px;
`

const PaymentPage = () => {
  const { creditGuardUrl, transactionComplete } = useTypedSelector(
    (state) => state.payment
  )
  const isDesktop = useIsDesktop()

  const [openModal, setOpenModal] = useState(false)

  const dispatch = useAppDispatch()
  const history = useHistory()
  const router = useRouter()

  const updatePaymentUrl = useCallback(async () => {
    // Get the current order ID from the order repository.
    const orderId = orderRepository.getOrderId()

    // If there's an order ID, fetch the payment URL for that order.
    if (orderId) {
      await dispatch(fetchPaymentUrl(orderId))
    } else {
      // If there isn't an order ID, throw an error.
      throw new Error("No order ID")
    }
  }, [dispatch])

  // When the transaction is complete...
  useEffect(() => {
    if (!transactionComplete) return

    // If the transaction was successful...
    if (transactionComplete.succeed) {
      // Clear the order ID
      orderRepository.clearOrderId()
      // Redirect to the success page
      router.replace("/shop/success")
    } else {
      // Otherwise, open the modal
      setOpenModal(true)
    }
  }, [transactionComplete, router])

  // On first mount, check if we have a payment URL
  useEffect(() => {
    if (creditGuardUrl) {
      // If we have a payment URL, track the checkout step
      trackCheckoutStep("5")
    } else {
      // If we don't have a payment URL, get one from the API
      updatePaymentUrl().catch(() => history.replace(routes.order.path))
    }
  }, [creditGuardUrl, updatePaymentUrl, history])

  // split useEffect so return function wont jump on creditGuardUrl value change
  useEffect(
    () => () => {
      dispatch(clearCreditGuardUrl())
    },
    [dispatch]
  )

  return (
    <>
      <Head>
        {ENV !== "production" ? (
          <>
            <script src="https://ppsuat.creditguard.co.il/plugins/applePayOnIframe.js"></script>
          </>
        ) : (
          <script src="https://pps.creditguard.co.il/plugins/applePayOnIframe.js"></script>
        )}
      </Head>
      {isDesktop && (
        <div>
          <StyledHeadingContainer>
            <StyledImageButton
              onClick={() => {
                history.push(routes.order.path)
              }}
            >
              <img src="/icons/forward-arrow.svg" />
            </StyledImageButton>
            <StyledHeading>{routes.payment.title}</StyledHeading>
          </StyledHeadingContainer>

          <StyledText>
            אל דאגה, המקדמה תוחזר לך במלואה אם בסופו של דבר לא תושלם הרכישה.
            <br />
            התשלום כעת מבטיח שנשמור לך את ההטבה ואת ההצעה
          </StyledText>
        </div>
      )}

      <PaymentFrame url={creditGuardUrl} />

      <ErrorDialog
        open={openModal}
        message={transactionComplete?.message || null}
        onClose={() => {
          setOpenModal(false)
          dispatch(resetTransactionComplete())
          updatePaymentUrl()
        }}
      />
    </>
  )
}

export default PaymentPage
